import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Microphone, Stop, Paperclip, FloppyDisk, Hash, X, Sparkle, MagicWand, FileText, Image as ImageIcon, CheckCircle, Spinner } from "@phosphor-icons/react";
import LocationPicker from "@/components/LocationPicker";

export default function DataEntry() {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [locations, setLocations] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [hashtagMaster, setHashtagMaster] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestPos, setSuggestPos] = useState({ top: 0, left: 0 });
  const [suggestQuery, setSuggestQuery] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [assistBusy, setAssistBusy] = useState(false);
  const [assistDraft, setAssistDraft] = useState(null); // { rewritten, latency_ms }
  const recogRef = useRef(null);
  const taRef = useRef(null);
  const slashIdxRef = useRef(-1);

  useEffect(() => {
    api.get("/hashtags").then((r) => setHashtagMaster(r.data));
  }, []);

  // --- voice (Web Speech API) ---
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice not supported in this browser");
      return;
    }
    const r = new SR();
    r.continuous = true;
    r.interimResults = false;
    r.lang = "en-IN";
    r.onresult = (e) => {
      let txt = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        txt += e.results[i][0].transcript + " ";
      }
      setContent((c) => (c ? c + " " : "") + txt.trim());
    };
    r.onerror = () => setRecording(false);
    r.onend = () => setRecording(false);
    r.start();
    recogRef.current = r;
    setRecording(true);
  };
  const stopVoice = () => {
    if (recogRef.current) recogRef.current.stop();
    setRecording(false);
  };

  // --- file upload + auto-extract ---
  const onFile = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      if (f.size > 2 * 1024 * 1024) {
        toast.error(`${f.name} too large (max 2MB)`);
        return;
      }
      const reader = new FileReader();
      reader.onload = async () => {
        const uid = `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const att = { uid, name: f.name, mime: f.type, size: f.size, data_url: reader.result, extracted_text: "", extracting: true };
        setAttachments((a) => [...a, att]);
        try {
          const { data } = await api.post("/extract", {
            data_url: reader.result, mime: f.type, name: f.name,
          });
          setAttachments((a) =>
            a.map((x) => x.uid === uid
              ? { ...x, extracted_text: data.extracted_text || "", extract_method: data.method, extract_ms: data.latency_ms, extracting: false }
              : x)
          );
          if (data.extracted_text) toast.success(`Extracted ${f.name} (${data.method}, ${data.latency_ms}ms)`);
        } catch (err) {
          setAttachments((a) => a.map((x) => x.uid === uid ? { ...x, extracting: false, extract_error: true } : x));
          toast.error(`Extraction failed: ${f.name}`);
        }
      };
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  // --- AI writing assist ---
  const runAssist = async () => {
    if (!content.trim()) {
      toast.error("Write something first, then ask the assistant");
      return;
    }
    setAssistBusy(true);
    setAssistDraft(null);
    try {
      const { data } = await api.post("/assist/expand", {
        text: content,
        location_id: locations[0] || null,
      });
      setAssistDraft(data);
    } catch (err) {
      toast.error("Assistant unavailable");
    } finally {
      setAssistBusy(false);
    }
  };
  const acceptAssist = () => {
    if (assistDraft) setContent(assistDraft.rewritten);
    setAssistDraft(null);
    toast.success("Polished narrative applied");
  };
  const rejectAssist = () => setAssistDraft(null);

  // append extracted text into content
  const appendExtract = (att) => {
    if (!att.extracted_text) return;
    setContent((c) => (c ? c + "\n\n" : "") + `--- from ${att.name} ---\n${att.extracted_text}`);
    toast.success(`Pulled ${att.name} into content`);
  };

  // --- hashtag autocomplete via "/" ---
  const onContentChange = (e) => {
    const v = e.target.value;
    setContent(v);
    const cursor = e.target.selectionStart;
    // find recent "/word" before cursor
    const upto = v.slice(0, cursor);
    const m = upto.match(/(?:^|[\s])\/(\w*)$/);
    if (m) {
      slashIdxRef.current = cursor - m[1].length - 1;
      setSuggestQuery(m[1]);
      setShowSuggest(true);
      // approx position - place dropdown below textarea
      setSuggestPos({ top: 0, left: 0 });
    } else {
      setShowSuggest(false);
    }
  };

  const insertTag = (tagName) => {
    const ta = taRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart;
    const before = content.slice(0, slashIdxRef.current);
    const after = content.slice(cursor);
    const ins = `#${tagName}`;
    const next = before + ins + after;
    setContent(next);
    if (!tags.includes(tagName)) setTags([...tags, tagName]);
    setShowSuggest(false);
    setTimeout(() => {
      ta.focus();
      const newPos = before.length + ins.length;
      ta.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const filteredSuggestions = hashtagMaster
    .filter((h) => h.tag_name.includes(suggestQuery.toLowerCase()))
    .slice(0, 6);

  // --- explicit hashtag chip add ---
  const addTagChip = () => {
    const t = tagInput.trim().toLowerCase().replace(/^#/, "");
    if (!t) return;
    if (!tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  };

  const submit = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error("Title and content required");
      return;
    }
    if (locations.length === 0) {
      toast.error("Pick at least one address");
      return;
    }
    setBusy(true);
    try {
      const cleanAttachments = attachments.map((a) => ({
        name: a.name, mime: a.mime, size: a.size,
        data_url: a.data_url, extracted_text: a.extracted_text || "",
      }));
      await api.post("/knowledge", {
        title,
        content,
        location_ids: locations,
        hashtags: tags,
        attachments: cleanAttachments,
      });
      toast.success("Knowledge filed to all selected addresses");
      setTitle("");
      setContent("");
      setLocations([]);
      setTags([]);
      setAttachments([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="data-entry-page">
      <div>
        <div className="mono-label text-orange-600 mb-2">// new knowledge filing</div>
        <h1 className="font-display text-4xl md:text-5xl text-slate-900 tracking-tight leading-tight">
          File a new fact.
        </h1>
        <p className="text-slate-600 mt-2">
          Choose its address, write or speak the content, drop attachments. Type{" "}
          <span className="font-mono bg-slate-100 px-1.5 py-0.5 border border-slate-200">/</span>{" "}
          inside the content to insert a hashtag from the master.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT - Address + meta */}
        <div className="space-y-6">
          <div className="border border-slate-200 bg-white p-6">
            <div className="mono-label mb-4 text-slate-900">Step 1 · Address</div>
            <LocationPicker selected={locations} onChange={setLocations} />
          </div>

          <div className="border border-slate-200 bg-white p-6">
            <div className="mono-label mb-3 text-slate-900">Step 2 · Hashtags</div>
            <div className="flex gap-2 mb-3">
              <input
                data-testid="tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTagChip())}
                placeholder="add a tag (no # needed)"
                className="flex-1 border border-slate-300 px-3 py-2 outline-none focus:border-slate-900 font-mono text-sm"
                style={{ borderRadius: 2 }}
              />
              <button onClick={addTagChip} type="button" className="btn-sharp btn-secondary text-sm" data-testid="add-tag-btn">
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1.5 bg-slate-900 text-white font-mono text-xs px-2 py-1"
                  data-testid={`chip-${t}`}
                >
                  <Hash size={10} />
                  {t}
                  <button onClick={() => setTags(tags.filter((x) => x !== t))} className="ml-1">
                    <X size={10} />
                  </button>
                </span>
              ))}
              {tags.length === 0 && <span className="text-slate-400 text-sm">No tags yet</span>}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="mono-label mb-2">Master tags ({hashtagMaster.length})</div>
              <div className="flex flex-wrap gap-1.5">
                {hashtagMaster.map((h) => (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => !tags.includes(h.tag_name) && setTags([...tags, h.tag_name])}
                    className="font-mono text-xs px-2 py-1 border border-slate-200 hover:border-slate-900 transition-all"
                    data-testid={`master-tag-${h.tag_name}`}
                  >
                    #{h.tag_name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT - Content */}
        <div className="space-y-6">
          <div className="border border-slate-200 bg-white p-6">
            <div className="mono-label mb-3 text-slate-900">Step 3 · Content</div>
            <input
              data-testid="title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title (e.g. Aashirvaad Multigrain Atta — Q1 pricing)"
              className="w-full border border-slate-300 px-4 py-3 mb-3 outline-none focus:border-slate-900 text-base text-slate-900"
              style={{ borderRadius: 2 }}
            />

            <div className="relative">
              <textarea
                data-testid="content-textarea"
                ref={taRef}
                value={content}
                onChange={onContentChange}
                rows={14}
                placeholder="Write the knowledge here. Use / to call hashtags from the master."
                className="w-full border border-slate-300 px-4 py-3 outline-none focus:border-slate-900 text-sm text-slate-900 font-mono leading-relaxed resize-none"
                style={{ borderRadius: 2 }}
              />
              {showSuggest && filteredSuggestions.length > 0 && (
                <div
                  className="absolute z-20 bg-white border border-slate-300 shadow-lg max-w-xs"
                  style={{ bottom: 60, left: 12 }}
                  data-testid="hashtag-suggest"
                >
                  <div className="mono-label px-3 py-2 border-b border-slate-200 bg-slate-50">
                    Hashtag master /{suggestQuery}
                  </div>
                  {filteredSuggestions.map((h) => (
                    <button
                      key={h.id}
                      onClick={() => insertTag(h.tag_name)}
                      className="block w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                      data-testid={`suggest-${h.tag_name}`}
                    >
                      <div className="font-mono text-sm text-slate-900">#{h.tag_name}</div>
                      <div className="text-xs text-slate-500 truncate">{h.description}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {!recording ? (
                <button onClick={startVoice} type="button" className="btn-sharp btn-secondary text-sm" data-testid="voice-start-btn">
                  <Microphone size={16} /> Start voice
                </button>
              ) : (
                <button onClick={stopVoice} type="button" className="btn-sharp btn-primary text-sm" data-testid="voice-stop-btn">
                  <Stop size={16} weight="fill" /> Stop ({"recording…"})
                </button>
              )}
              <label className="btn-sharp btn-secondary text-sm cursor-pointer" data-testid="attachment-label">
                <Paperclip size={16} /> Attach
                <input type="file" multiple onChange={onFile} className="hidden" data-testid="file-input" accept=".pdf,image/*,.txt,.md,.csv" />
              </label>
              <button
                onClick={runAssist}
                type="button"
                disabled={assistBusy}
                className="btn-sharp text-sm border bg-slate-900 text-white border-slate-900 hover:bg-slate-800 transition-all disabled:opacity-50 inline-flex items-center gap-2"
                style={{ borderRadius: 2 }}
                data-testid="ai-assist-btn"
              >
                {assistBusy ? <Spinner size={14} className="animate-spin" /> : <MagicWand size={14} weight="fill" className="text-orange-400" />}
                {assistBusy ? "Polishing…" : "AI Assist · Polish"}
              </button>
              <span className="mono-label ml-auto">{content.length} chars</span>
            </div>

            {/* AI assist preview */}
            {assistDraft && (
              <div className="mt-3 border border-slate-900 bg-slate-50" data-testid="assist-preview">
                <div className="px-4 py-2 bg-slate-900 text-white flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkle size={14} weight="fill" className="text-orange-400" />
                    <span className="mono-label text-slate-300">Polished narrative</span>
                  </div>
                  <span className="font-mono text-xs text-slate-400">
                    {assistDraft.latency_ms}ms · Claude Sonnet 4.5
                  </span>
                </div>
                <div className="px-4 py-3 max-h-72 overflow-y-auto text-sm text-slate-900 font-mono leading-relaxed whitespace-pre-wrap">
                  {assistDraft.rewritten}
                </div>
                <div className="px-4 py-2 border-t border-slate-200 flex items-center justify-end gap-2">
                  <button onClick={rejectAssist} className="btn-sharp btn-secondary text-xs" data-testid="assist-reject-btn">
                    Discard
                  </button>
                  <button onClick={acceptAssist} className="btn-sharp btn-primary text-xs" data-testid="assist-accept-btn">
                    <CheckCircle size={12} weight="bold" /> Use this version
                  </button>
                </div>
              </div>
            )}

            {attachments.length > 0 && (
              <div className="mt-4 space-y-2">
                {attachments.map((a) => (
                  <div key={a.uid} className="border border-slate-200" data-testid={`attachment-${a.uid}`}>
                    <div className="flex items-center justify-between px-3 py-2 text-sm font-mono bg-white">
                      <div className="flex items-center gap-2 min-w-0">
                        {a.mime?.includes("pdf") ? <FileText size={14} className="text-red-600" /> : a.mime?.startsWith("image/") ? <ImageIcon size={14} className="text-blue-600" /> : <Paperclip size={14} className="text-slate-500" />}
                        <span className="truncate">{a.name}</span>
                        <span className="text-slate-400 shrink-0">· {Math.round(a.size / 1024)}KB</span>
                        {a.extracting && (
                          <span className="mono-label text-orange-600 flex items-center gap-1">
                            <Spinner size={10} className="animate-spin" /> extracting…
                          </span>
                        )}
                        {!a.extracting && a.extracted_text && (
                          <span className="mono-label text-emerald-600">
                            ✓ {a.extract_method} · {a.extract_ms}ms
                          </span>
                        )}
                        {a.extract_error && <span className="mono-label text-red-600">✗ failed</span>}
                      </div>
                      <button onClick={() => setAttachments(attachments.filter((x) => x.uid !== a.uid))} className="text-orange-600 ml-2 shrink-0">
                        <X size={14} />
                      </button>
                    </div>
                    {a.extracted_text && (
                      <div className="border-t border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="mono-label">Extracted text · indexed for RAG</span>
                          <button onClick={() => appendExtract(a)} className="mono-label text-orange-600 hover:underline" data-testid={`append-extract-${a.uid}`}>
                            ↳ append to content
                          </button>
                        </div>
                        <div className="text-xs text-slate-700 font-mono leading-relaxed whitespace-pre-wrap max-h-40 overflow-y-auto">
                          {a.extracted_text}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            data-testid="save-knowledge-btn"
            onClick={submit}
            disabled={busy}
            className="w-full btn-sharp btn-primary py-4 font-semibold text-base disabled:opacity-50"
          >
            <FloppyDisk size={20} weight="bold" />
            {busy ? "Filing…" : "File knowledge to complex"}
          </button>
        </div>
      </div>
    </div>
  );
}
